// TypeScript interfaces for Work Order module

// Approval workflow enums and interfaces
export enum ApprovalStatus {
  Draft = "Draft",
  Pending = "Pending",
  SubmittedForApproval = "Submitted for Approval",
  Checked = "Checked",
  Verified = "Verified",
  Approved = "Approved",
  ReturnedForCorrection = "Returned for Correction",
  Released = "Released",
}

export enum UserRole {
  Creator = "Creator",
  Checker = "Checker",
  Verifier = "Verifier",
  Approver = "Approver",
}

export interface ApprovalHistoryItem {
  action: string;
  actionBy: string;
  actionDate: string;
  role: UserRole;
  comments?: string;
}

export interface PendingWorkOrderData {
  id: string;
  workOrderNumber: string;
  workOrderTitle: string;
  workOrderDescription?: string;
  siteId: string;
  siteName: string;
  projectId?: string;
  projectName?: string;
  contractType: string;
  contractWith: string;
  contractorId: string;
  contractorName: string;
  workOrderOwner: string;
  plannedStartDate: string;
  plannedEndDate: string;
  totalValue: number;
  taxAmount: number;
  grossValue: number;
  status: ApprovalStatus;
  createdAt: string;
  createdBy: string;
  lastActionBy: string;
  lastActionDate: string;
  lastActionRole: UserRole;
  remarks?: string;
  approvalHistory: ApprovalHistoryItem[];
}

export interface WorkOrderData {
  id: string;
  workOrderNumber: string;
  linkedWorkRequest?: string;
  linkedTender?: string;
  contractDate: string;
  workOrderTitle: string;
  contractorApplicationAccess: boolean;
  workOrderDescription?: string;
  siteId: string;
  siteName: string;
  projectId?: string;
  projectName?: string;
  contractType: ContractType;
  contractWith: ContractWith;
  contractorId: string;
  contractorName: string;
  workOrderOwner: string;
  plannedStartDate: string;
  plannedEndDate: string;
  approvalRequired: boolean;
  enableTax: boolean;
  taxApplicationLevel?: any;
  headerTaxId?: number;
  headerTaxName?: string;
  tdsTaxId?: number;
  tdsTaxName?: string;
  tdsRate?: number;
  tcsTaxId?: number;
  tcsTaxName?: string;
  tcsRate?: number;
  budgetId?: number;
  budgetName?: string;
  workType?: string;
  executionStatus?: string;
  progressPercent?: number;
  sourceType?: string; // Source type: "Project Tasks", "Work BOQ", "New Work Line"
  segmentLevel?: string; // Work Level Settings for New Work Line
  status: WorkOrderStatus;
  workOrderLines: WorkOrderLine[];
  generalConditions: GeneralCondition[];
  paymentConditions: PaymentConditions | PaymentCondition[]; // Support both formats
  totalValue: number;
  taxAmount: number;
  grossValue: number;
  createdAt: string;
  createdBy: string;
  updatedAt?: string;
  approverComments?: string;
  rejectionComments?: string;
  // Additional fields from API response
  workOrderOwnerId?: string;
  workOrderOwnerName?: string;
  description?: string;
  outstandingAmount?: number;
  updatedBy?: string;
  orgName?: string;
  orgId?: string;
  attachments?: any[];
  dms?: any[];
  contractorChangeRequests?: any[];
  approvals?: any[];
  riskflassItems?: any[];
  advanceItems?: any[];
  workOrderApprovals?: ApprovalItem[];
}

export interface ApprovalItem {
  workOrderApprovalId?: number;
  userId: number;
  userName: string;
  roleName: string;
  approverName: string;
  approverRole: string;
  level: string;
  status: string;
  approvalStage?: string;
  approvedByName?: string;
  reason?: string;
  createdAt?: string;
}

export interface WorkOrderLine {
  id: string;
  lineNumber: string;
  workCategory: string;
  workCategoryName?: string; // Work category name for display (used in bulk upload)
  subCategory?: string;
  taskTitle: string;
  taskDescription?: string;
  quantity: number;
  uom: string;
  uomName?: string; // UOM name for display (used in bulk upload)
  weightagePercentage: number;
  rate: number;
  taskRate?: number; // Original task rate for reference (PM source)
  boqRate?: number; // Original BOQ rate for reference (Work BOQ source)
  rateChangeRemarks?: string; // Remarks when rate is changed from task/BOQ rate
  remarks?: string; // Store remarks for next steps (same as rateChangeRemarks)
  taxType?: string; // Tax ID for API submission
  taxTypeName?: string; // Tax label for display
  taxPercentage?: number;
  taxAmount: number;
  totalAmount: number;
  // GST specific fields
  sgstRate?: number; // State GST rate (same state)
  cgstRate?: number; // Central GST rate (same state)
  igstRate?: number; // Integrated GST rate (different states)
  source: WorkOrderLineSource;
  sourceId?: string; // PM task ID, BOQ line ID, etc.
  sectionId?: string;
  executedQuantity?: number;
  remainingQuantity?: number;
  completionPercentage?: number;
  // Budget allocation fields
  budgetLineId?: number;
  budgetLineName?: string;
  budgetAllocation?: number;
  // Work Level Settings location fields
  workLevel?: string;
  towerVilla?: string;
  towerVillaName?: string;
  floorName?: string;
  flatName?: string;
  flatNameDisplay?: string;
  locationId?: string;
  locationName?: string;
  // Work type field (only for New Work Line source)
  workType?: WorkType;
  // Bulk upload error tracking (only used in work order details step)
  errorFlag?: boolean;
  errorMessage?: string | string[];
  // Schedule breakdown stages
  stages?: WorkOrderLineStage[];
  deletedStageIds?: number[];

  //
  scheduledStartDate?: string,
  scheduledEndDate?: string
}

export interface WorkOrderLineStage {
  workOrderLineStageId?: number; // Only for existing stages during update
  stageTitle: string;
  splitPercentage: number;
}

export interface GeneralCondition {
  id: string;
  conditionName: string;
  conditionContent: string;
  isActive: boolean;
  order: number;
  attachments?: ConditionAttachment[];
}

export interface ConditionAttachment {
  id: string;
  fileName: string;
  fileSize: number;
  fileType: string;
  uploadDate: string;
  url?: string;
}

export interface PaymentConditions {
  mobilizationAdvance: MobilizationAdvance;
  retentionAmount: RetentionAmount;
  onHoldAmount: OnHoldAmount;
  payOnWorkCompletion: boolean;
}

// New interface for API response format - array of payment conditions
export interface PaymentCondition {
  id: string;
  conditionType: "Mobilization" | "Retention" | "On Hold";
  isPercent: boolean;
  percentValue: number;
  amountValue: number;
  interestCharged: boolean;
  interestRate: number;
  recoveryType: string;
  recoveryPercentRate?: number;
  recoveryCapPercent?: number;
  releaseDate: string;
  notes: string;
  calculatedAmount: number;
  createdAt: string;
  updatedAt: string;
}

export interface MobilizationAdvance {
  workOrderPaymentTermId?: number; // Database ID for updates
  isPercentOfContract: boolean;
  percentageOfContract?: number;
  advanceValue?: number;
  interestCharged: boolean;
  interestRate?: number;
  recoveryType: any;
  recoveryPercentRate?: number;
  recoveryCapPercent?: number; // Upto what % of work order value the recovery should be considered
}

export interface RetentionAmount {
  workOrderPaymentTermId?: number; // Database ID for updates
  isPercentOfRABill: boolean;
  percentageOfRABill?: number;
  retentionValue?: number;
  dlpAmount?: number;
  dlpUnit?: "days" | "months" | "years";
  releaseDate: any;
}

export interface OnHoldAmount {
  workOrderPaymentTermId?: number; // Database ID for updates
  isPercentOfContract: boolean;
  percentageOfContract?: number;
  holdAmountValue?: number;
  dlpAmount?: number;
  dlpUnit?: "days" | "months" | "years";
  releaseDate: any;
}

// Project Management related interfaces
export interface ProjectTask {
  taskId: string;
  taskName: string;
  workCategory: string;
  description: string;
  quantity: number;
  uom: string;
  weightagePercentage: number;
  rate: number;
  taxType: string;
  taxPercentage: number;
  activityId: string;
  activityName: string;
  projectId: string;
  isSelected?: boolean;
  // Budget allocation fields for Project Management source
  budgetHeaderId?: number;
  budgetLineId?: number;
  budgetValue?: number;
  budgetLineName?: string;
  WorkOrderAllocatedBudget?: number;
  // Location fields from task API response (for Project Management tasks)
  siteStructureUnitId?: number | null;
  siteStructureUnitName?: string;
  siteStructureSubUnitId?: number | null;
  siteStructureSubUnitName?: string;
  floorNo?: string | null;
  locationId?: number | null;
  locationName?: string;
}

// Work BOQ related interfaces
export interface WorkBoqLine {
  lineId: string;
  taskName: string;
  workCategory: string;
  approvedQuantity: number;
  allocatedQuantity: number;
  pendingQuantity: number;
  uom: string;
  weightagePercentage: number;
  rate: number;
  taxType: string;
  taxPercentage: number;
  workBoqId: string;
  isSelected?: boolean;
  // Budget allocation fields
  budgetAllocation?: number;
  budgetLineId?: number;
  budgetLineName?: string;
  workOrderAllocatedBudget?: number;
}

// Master data interfaces
export interface Site {
  id: string;
  name: string;
}

export interface Project {
  id: string;
  name: string;
  siteId: string;
}

export interface Activity {
  id: string;
  name: string;
  projectId: string;
  description?: string;
}

export interface WorkBoq {
  id: string;
  title: string;
  siteId: string;
  projectId?: string;
  status: string;
}

export interface Contractor {
  id: string;
  companyName: string;
  contactPerson: string;
  email: string;
  phone: string;
  siteIds: string[];
}

export interface WorkCategory {
  id: string;
  name: string;
  parentId?: string;
  description?: string;
}

export interface UOM {
  id: string;
  name: string;
  code: string;
}

export interface TaxType {
  id: string;
  name: string;
  percentage: number;
  code: string;
}

export interface User {
  id: string;
  name: string;
  email: string;
  role: string;
}

export interface WorkRequest {
  id: string;
  workRequestNumber: string;
  workRequestTitle: string;
  siteId: string;
  status: string;
}

export interface Tender {
  id: string;
  tenderNumber: string;
  tenderTitle: string;
  siteId: string;
  status: string;
}

export interface MasterData {
  sites: Site[];
  projects: Project[];
  activities: Activity[];
  workBoqs: WorkBoq[];
  contractors: Contractor[];
  workCategories: WorkCategory[];
  uomList: UOM[];
  taxTypes: TaxType[];
  users: User[];
  workRequests: WorkRequest[];
  tenders: Tender[];
}

// Predefined general conditions
export interface PredefinedCondition {
  id: string;
  name: string;
  defaultContent: string;
  order: number;
}

// Enums
export enum ContractType {
  Construction = "Construction Work",
  Service = "Service",
  Maintenance = "Maintenance",
  Others = "Others",
}

export enum ContractWith {
  GeneralContractor = "General Contractor",
  SubContractor = "Sub Contractor",
  Others = "Others",
}

export enum TaxApplicationLevel {
  Header = "Header",
  Line = "Line",
  Both = "Both",
}

export enum WorkOrderStatus {
  Draft = "Draft",
  PendingApproval = "Pending Approval",
  Rejected = "Rejected",
  Approved = "Approved",
  Released = "Released",
  ContractorConfirmed = "Contractor Confirmed",
  InExecution = "In Execution",
  OnHold = "On Hold",
  VariationPending = "Pending Execution Approval",
  Amended = "Amended",
  Completed = "Completed",
  Closed = "Closed",
  Cancelled = "Cancelled",
  ChangesRequested = "Changes Requested",
}

export enum WorkOrderLineSource {
  ProjectManagement = "Project Management",
  WorkBOQ = "Work BOQ",
  NewWorkLine = "New Work Line",
}

export enum WorkType {
  Open = "Open",
  QTY = "Quantity",
}

export enum RecoveryType {
  EachRABill = "Each RA Bill",
  FinalBill = "Final Bill",
}

// Form interfaces
export interface WorkOrderFormData {
  // General Information (Section A)
  workOrderNumber: string;
  linkedWorkRequest?: string;
  linkedTender?: string;
  contractDate: any;
  workOrderTitle: string;
  budgetHeaderId?: string;
  budgetHeaderName?: string;
  workOrderDescription?: string;
  siteId: string;
  siteName?: string;
  projectId?: string;
  projectName?: string;
  contractType: ContractType | string;
  contractWith: ContractWith | string;
  contractorId: string;
  contractorName?: string;
  budgetId?: string;
  workOrderOwner: string;
  workOrderOwnerName?: string;
  plannedStartDate: any;
  plannedEndDate: any;
  approvalRequired: string; // "true" | "false" for form handling
  enableTax: string; // "true" | "false" for form handling
  taxApplicationLevel?: TaxApplicationLevel | string;

  // Work Order Information (Section B)
  selectedSource?: WorkOrderLineSource;
  activityId?: string; // For Project Management source
  workCategoryId?: string; // For Project Management source
  workBoqId?: string; // For Work BOQ source
  workLevel?: string; // Work Level Settings for New Work Line source
  workType?: WorkType | string; // Work Type for New Work Line source (Open or QTY)
  workOrderLines: WorkOrderLine[];

  // General Conditions (Section C)
  generalConditions: GeneralCondition[];

  // Payment Conditions (Section D)
  paymentConditions: PaymentConditions;

  // Internal fields
  status?: WorkOrderStatus;
  type?: "create" | "edit" | "duplicate";
  createdBy?: string;
  createdAt?: string;
  approvalsData?: any;
}

// Payment Conditions form interfaces
export interface PaymentConditionsFormData {
  // Mobilization Advance
  mobilizationIsPercent: string; // "true" | "false"
  mobilizationPercentage?: number;
  mobilizationValue?: number;
  mobilizationInterestCharged: string; // "true" | "false"
  mobilizationInterestRate?: number;
  mobilizationRecoveryType: RecoveryType | any;

  // Retention Amount
  retentionIsPercent: string; // "true" | "false"
  retentionPercentage?: number;
  retentionValue?: number;
  retentionReleaseDate: string | null;

  // On Hold Amount
  onHoldIsPercent: string; // "true" | "false"
  onHoldPercentage?: number;
  onHoldValue?: number;
  onHoldReleaseDate: string | null;

  // Pay on Work Completion
  payOnWorkCompletion: string; // "true" | "false"
}

// Filter interfaces
export interface WorkOrderFilters {
  searchText?: string;
  siteIds?: string[];
  projectIds?: string[];
  statuses?: WorkOrderStatus[];
  contractTypes?: ContractType[];
  contractorIds?: string[];
  contractWith?: ContractWith[];
  approvalRequired?: boolean;
  enableTax?: boolean;
  workOrderValueRange?: {
    min: number;
    max: number;
  };
  dateRange?: {
    startDate: string;
    endDate: string;
  };
  createdBy?: string[];
}

// API Response interfaces
export interface ApiResponse<T> {
  data: T;
  status: boolean;
  message: string;
  pagination?: {
    current: number;
    pageSize: number;
    total: number;
  };
}

// Component Props interfaces
export interface WorkOrderTableProps {
  data: WorkOrderData[];
  loading?: boolean;
  onEdit?: (record: WorkOrderData) => void;
  onView?: (record: WorkOrderData) => void;
  onDelete?: (record: WorkOrderData) => void;
  onSubmit?: (record: WorkOrderData) => void;
  onApprove?: (record: WorkOrderData) => void;
  onReject?: (record: WorkOrderData) => void;
  onRelease?: (record: WorkOrderData) => void;
  onCancel?: (record: WorkOrderData) => void;
}

export interface TaskSelectorProps {
  activityId?: string;
  workCategoryId?: string;
  workBoqId?: string;
  onLinesSelected: (lines: WorkOrderLine[]) => void;
  selectedLines?: WorkOrderLine[];
  editingLine?: WorkOrderLine;
  siteId?: string;
}

export interface GeneralConditionsProps {
  conditions: GeneralCondition[];
  onConditionsChange: (conditions: GeneralCondition[]) => void;
}

export interface PaymentConditionsProps {
  conditions: PaymentConditions;
  onConditionsChange: (conditions: PaymentConditions) => void;
}

// Step component props
export interface StepComponentProps {
  next?: (data?: any) => void;
  prev?: (data?: any) => void;
  current?: number;
  [key: string]: any;
}

// Execution related interfaces (for future implementation)
export interface ExecutionBatch {
  id: string;
  workOrderId: string;
  sectionId: string;
  executionPeriodFrom: string;
  executionPeriodTo: string;
  notes?: string;
  attachments: string[];
  lines: ExecutionLine[];
  status: ExecutionBatchStatus;
  submittedBy: string;
  submittedAt: string;
  checkerComments?: string;
  verifierComments?: string;
  approverComments?: string;
}

export interface ExecutionLine {
  workLineId: string;
  executedQuantity: number;
  remarks?: string;
}

export enum ExecutionBatchStatus {
  Draft = "Draft",
  Submitted = "Submitted",
  Checked = "Checked",
  Verified = "Verified",
  Approved = "Approved",
  Rejected = "Rejected",
}

// Section interface for execution
export interface WorkOrderSection {
  id: string;
  name: string;
  workOrderId: string;
  lines: WorkOrderLine[];
  progressPercentage: number;
  lastUpdate?: string;
}
